import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _sortBy from 'lodash/sortBy';
import _filter from 'lodash/filter';
import { gqlPetEnum as PET } from '@minecraft.graphql-types';
import ErrorMessage from '@minecraft.error-message';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import { useTranslation } from '@blocs.i18n';
import { FiltersNameEnum } from '../../../shared/types';
import { generateSelectOptionsBasedOnField } from '../../../shared/utils';
import { FormItem } from '../../../shared/styles';
import { FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { useFiltersLabel } from '../../shared/hooks/useFiltersLabel';
import { FilterTitle, Delimiter } from '../../shared/styles';
import {
  DogBreed,
  AnimalSize,
  OtherAnimals,
  PropsFilter,
  SportsEquipment,
  Wardrobe,
} from '../../FilterDrawerCollection';
import { QUERY_ASSETS } from './AssetsCategoryGraphQL';

interface Props {
  type: FilterDrawerType;
}

const AssetsCategory: FC<Props> = ({ type }) => {
  const { t } = useTranslation();
  const filtersLabelOfAudience = useFiltersLabel(type);

  const { data, error, loading } = useQuery(QUERY_ASSETS);

  const { isSearching, isCategoryMatchedSearch, shouldCategoryFilterBeVisible, assignOptionsToCategoryFilters } =
    useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.assets);
  const { filtersCounts } = useDrawerFiltersCounts();

  const {
    dogBreedOptions,
    otherAnimalsOptions,
    animalSizeOptions,
    propOptions,
    wardrobeOptions,
    sportEquipmentOptions,
  } = useMemo(() => {
    const { petSizes = [], pets = [], props = [], sportEquipments = [], wardrobes = [] } = data || {};

    return {
      dogBreedOptions: generateSelectOptionsBasedOnField(
        _sortBy(
          _filter(pets, (pet) => pet?.parentObject?.code === PET.DOG),
          'name'
        ),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.petIds]
      ),
      otherAnimalsOptions: generateSelectOptionsBasedOnField(
        _sortBy(
          _filter(pets, (pet) => pet?.parentObject?.code !== PET.DOG),
          'name'
        ),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.petIds]
      ),
      animalSizeOptions: generateSelectOptionsBasedOnField(
        _sortBy(petSizes, 'name'),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.petSizeId]
      ),
      propOptions: generateSelectOptionsBasedOnField(
        _sortBy(
          _filter(props, (pet) => pet.parentObject),
          'name'
        ),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.propIds]
      ),
      wardrobeOptions: generateSelectOptionsBasedOnField(
        _sortBy(
          _filter(wardrobes, (pet) => pet.parentObject),
          'name'
        ),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.wardrobeIds]
      ),
      sportEquipmentOptions: generateSelectOptionsBasedOnField(
        _sortBy(
          _filter(sportEquipments, (pet) => pet.parentObject),
          'name'
        ),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.sportEquipmentIds]
      ),
    };
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (dogBreedOptions.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.dogBreed]: dogBreedOptions,
        [FiltersNameEnum.dogBreedSize]: animalSizeOptions,
        [FiltersNameEnum.otherAnimals]: otherAnimalsOptions,
        [FiltersNameEnum.props]: propOptions,
        [FiltersNameEnum.sportsEquipment]: sportEquipmentOptions,
        [FiltersNameEnum.wardrobe]: wardrobeOptions,
      });
    }
  }, [dogBreedOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <FormItem $fullWidth $marginValue={2}>
        {isSearching && <FilterTitle>{t('common:filtersName.assets')}</FilterTitle>}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.dogBreed) && (
          <FormItem $fullWidth $marginValue={2}>
            <DogBreed name={FiltersNameEnum.dogBreed} label={filtersLabelOfAudience[FiltersNameEnum.dogBreed]} />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.dogBreedSize) && (
          <FormItem $fullWidth $marginValue={2}>
            <AnimalSize
              name={FiltersNameEnum.dogBreedSize}
              label={filtersLabelOfAudience[FiltersNameEnum.dogBreedSize]}
            />
          </FormItem>
        )}
        <Delimiter />
        {shouldCategoryFilterBeVisible(FiltersNameEnum.otherAnimals) && (
          <FormItem $fullWidth $marginValue={2}>
            <OtherAnimals
              name={FiltersNameEnum.otherAnimals}
              label={filtersLabelOfAudience[FiltersNameEnum.otherAnimals]}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.props) && (
          <FormItem $fullWidth $marginValue={2}>
            <PropsFilter
              name={FiltersNameEnum.props}
              label={filtersLabelOfAudience[FiltersNameEnum.props]}
              subLabel={t('common:filtersName.props.subLabel')}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.sportsEquipment) && (
          <FormItem $fullWidth $marginValue={2}>
            <SportsEquipment
              name={FiltersNameEnum.sportsEquipment}
              label={filtersLabelOfAudience[FiltersNameEnum.sportsEquipment]}
              subLabel={t('common:filtersName.sportsEquipment.subLabel')}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.wardrobe) && (
          <FormItem $fullWidth $marginValue={2}>
            <Wardrobe
              name={FiltersNameEnum.wardrobe}
              label={filtersLabelOfAudience[FiltersNameEnum.wardrobe]}
              subLabel={t('common:filtersName.wardrobe.subLabel')}
            />
          </FormItem>
        )}
      </FormItem>
    )
  );
};

export default AssetsCategory;
